import type {
  AggregationFunctionAttribute,
  AppDataTypeAllowedAggregationAttribute,
  AppDataTypeAllowedFilterAttribute,
  AppDataTypeAttribute,
  FilterOperatorAttribute,
  RecordAttributes,
  RecordsBootstrapResponse,
} from '../recordAttributes'
import type { AggregationType, FilterOperator } from '@features/views/models'

type RawRecordAttributes = Omit<RecordsBootstrapResponse, 'app_data_types'> & {
  attributes?: Record<string, unknown>
  metadata?: Record<string, unknown>
  meta?: Record<string, unknown>
  recordAttributes?: Record<string, unknown>
  data_type_capabilities?: Record<string, unknown>
  dataTypeCapabilities?: Record<string, unknown>
  app_data_types?: unknown
  appDataTypes?: unknown
  filterOperators?: unknown
  aggregationFunctions?: unknown
  appDataTypeAllowedFilters?: unknown
  appDataTypeAllowedAggregations?: unknown
  allowed_filters?: unknown
  allowed_aggregations?: unknown
  app_data_type_filters?: unknown
  app_data_type_aggregations?: unknown
  app_data_type_filter_operators?: unknown
  app_data_type_aggregation_functions?: unknown
  app_data_type_allowed_filters_view?: unknown
  app_data_type_allowed_aggregations_view?: unknown
}

type RawItem = Record<string, unknown>
type NormalizedAllowedFilter = AppDataTypeAllowedFilterAttribute & RawItem
type NormalizedAllowedAggregation = AppDataTypeAllowedAggregationAttribute & RawItem

const EMPTY_RECORD_ATTRIBUTES: RecordAttributes = {
  app_data_types: [],
  filter_operators: [],
  aggregation_functions: [],
  app_data_type_allowed_filters: [],
  app_data_type_allowed_aggregations: [],
}

export function normalizeRecordAttributes(data: RawRecordAttributes): RecordAttributes {
  const source = getAttributeSource(data)
  const appDataTypeEntries = getAppDataTypeEntries(source)
  const allowedFilters = normalizeAllowedFilters([
    ...getArray(
      source,
      'app_data_type_allowed_filters',
      'appDataTypeAllowedFilters',
      'app_data_type_allowed_filters_view',
      'appDataTypeAllowedFiltersView',
      'app_data_type_filters',
      'appDataTypeFilters',
      'app_data_type_filter_operators',
      'appDataTypeFilterOperators',
      'allowed_filters'
    ),
    ...getNestedArrays(appDataTypeEntries, 'allowed_filters', 'allowedFilters'),
    ...getNestedArrays(appDataTypeEntries, 'filter_operators', 'filterOperators'),
  ])
  const allowedAggregations = normalizeAllowedAggregations([
    ...getArray(
      source,
      'app_data_type_allowed_aggregations',
      'appDataTypeAllowedAggregations',
      'app_data_type_allowed_aggregations_view',
      'appDataTypeAllowedAggregationsView',
      'app_data_type_aggregations',
      'appDataTypeAggregations',
      'app_data_type_aggregation_functions',
      'appDataTypeAggregationFunctions',
      'allowed_aggregations'
    ),
    ...getNestedArrays(appDataTypeEntries, 'allowed_aggregations', 'allowedAggregations'),
    ...getNestedArrays(appDataTypeEntries, 'aggregation_functions', 'aggregationFunctions'),
  ])
  const appDataTypes = normalizeAppDataTypes(
    [...getArray(source, 'app_data_types', 'appDataTypes', 'data_types'), ...appDataTypeEntries],
    allowedFilters,
    allowedAggregations
  )

  return {
    ...EMPTY_RECORD_ATTRIBUTES,
    app_data_types: appDataTypes,
    filter_operators: normalizeFilterOperators(
      getArray(source, 'filter_operators', 'filterOperators'),
      allowedFilters
    ),
    aggregation_functions: normalizeAggregationFunctions(
      getArray(source, 'aggregation_functions', 'aggregationFunctions'),
      allowedAggregations
    ),
    app_data_type_allowed_filters: allowedFilters.map((item) => ({
      app_data_type_id: item.app_data_type_id,
      filter_operator_id: item.filter_operator_id,
    })),
    app_data_type_allowed_aggregations: allowedAggregations.map((item) => ({
      app_data_type_id: item.app_data_type_id,
      aggregation_function_id: item.aggregation_function_id,
    })),
  }
}

function getAttributeSource(data: RawRecordAttributes): Record<string, unknown> {
  const raw = data as Record<string, unknown>
  for (const key of [
    'attributes',
    'settings',
    'data',
    'result',
    'payload',
    'capabilities',
    'recordAttributes',
    'data_type_capabilities',
    'dataTypeCapabilities',
    'metadata',
    'meta',
  ] as const) {
    const value = raw[key]
    if (isObject(value)) return value
  }

  return { ...raw }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getArray(source: Record<string, unknown>, ...keys: string[]): RawItem[] {
  for (const key of keys) {
    const value = source[key]
    if (Array.isArray(value)) return value.filter(isObject)
  }

  return []
}

function getAppDataTypeEntries(source: Record<string, unknown>): RawItem[] {
  const value = source.app_data_types ?? source.appDataTypes
  if (Array.isArray(value)) return value.filter(isObject)
  if (!isObject(value)) return []

  return Object.entries(value).flatMap(([appDataTypeId, entry]) => {
    if (!isObject(entry)) return []

    return [
      {
        app_data_type_id: appDataTypeId,
        ...entry,
      },
    ]
  })
}

function getNestedArrays(items: RawItem[], ...keys: string[]): RawItem[] {
  return items.flatMap((item) => {
    for (const key of keys) {
      const value = item[key]
      if (Array.isArray(value)) return value.filter(isObject)
    }

    return []
  })
}

function firstString(item: RawItem, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = item[key]
    if (typeof value === 'string' && value.trim()) return value
  }

  return null
}

function firstBoolean(item: RawItem, fallback: boolean, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = item[key]
    if (typeof value === 'boolean') return value
  }

  return fallback
}

function normalizeAppDataTypes(
  items: RawItem[],
  allowedFilters: RawItem[] = [],
  allowedAggregations: RawItem[] = []
): AppDataTypeAttribute[] {
  const byId = new Map<string, AppDataTypeAttribute>()

  for (const item of [...items, ...allowedFilters, ...allowedAggregations]) {
    const id = firstString(item, 'id', 'app_data_type_id', 'appDataTypeId')
    const name = firstString(item, 'name', 'app_data_type_name', 'appDataTypeName')
    if (id && name && !byId.has(id)) {
      byId.set(id, { id, name })
    }
  }

  return [...byId.values()]
}

function normalizeFilterOperators(items: RawItem[], allowedFilters: NormalizedAllowedFilter[]) {
  const operatorItems = items.length > 0 ? items : allowedFilters
  const byId = new Map<string, FilterOperatorAttribute>()

  for (const item of operatorItems) {
    const id = firstString(item, 'id', 'filter_operator_id', 'filterOperatorId')
    const key = firstString(item, 'key', 'filter_operator_key', 'filterOperatorKey')
    if (!id || !key) continue

    byId.set(id, {
      id,
      key: key as FilterOperator,
      name:
        firstString(item, 'name', 'label', 'filter_operator_label', 'filterOperatorLabel') ?? key,
      requires_value: firstBoolean(item, true, 'requires_value', 'requiresValue'),
    })
  }

  return [...byId.values()]
}

function normalizeAggregationFunctions(
  items: RawItem[],
  allowedAggregations: NormalizedAllowedAggregation[]
) {
  const functionItems = items.length > 0 ? items : allowedAggregations
  const byId = new Map<string, AggregationFunctionAttribute>()

  for (const item of functionItems) {
    const id = firstString(item, 'id', 'aggregation_function_id', 'aggregationFunctionId')
    const key = firstString(item, 'key', 'aggregation_function_key', 'aggregationFunctionKey')
    if (!id || !key) continue

    byId.set(id, {
      id,
      key: key as AggregationType,
      name:
        firstString(
          item,
          'name',
          'label',
          'aggregation_function_label',
          'aggregationFunctionLabel'
        ) ?? key,
    })
  }

  return [...byId.values()]
}

function normalizeAllowedFilters(items: RawItem[]): NormalizedAllowedFilter[] {
  return items.flatMap((item) => {
    const appDataTypeId = firstString(item, 'app_data_type_id', 'appDataTypeId')
    const filterOperatorId = firstString(item, 'filter_operator_id', 'filterOperatorId')
    if (!appDataTypeId || !filterOperatorId) return []

    return [
      {
        ...item,
        app_data_type_id: appDataTypeId,
        filter_operator_id: filterOperatorId,
      },
    ]
  })
}

function normalizeAllowedAggregations(items: RawItem[]): NormalizedAllowedAggregation[] {
  return items.flatMap((item) => {
    const appDataTypeId = firstString(item, 'app_data_type_id', 'appDataTypeId')
    const aggregationFunctionId = firstString(
      item,
      'aggregation_function_id',
      'aggregationFunctionId'
    )
    if (!appDataTypeId || !aggregationFunctionId) return []

    return [
      {
        ...item,
        app_data_type_id: appDataTypeId,
        aggregation_function_id: aggregationFunctionId,
      },
    ]
  })
}
